import { onAuthChange } from "../lib/auth";
import { lang, onLangChange, t, toggleLang, applyToDocument } from "../lib/i18n";
import { versionLabel } from "../lib/version";
import { h, mount } from "./dom";
import { navigate, refresh, startRouter, currentRoute } from "./router";
import { renderHome } from "../screens/home";
import { renderBrowse } from "../screens/browse";
import { renderCollection } from "../screens/collection";
import { renderCollectionForm } from "../screens/collection-form";
import { renderStudy } from "../screens/study";
import { renderSignIn } from "../screens/signin";
import { renderShareClaim } from "../screens/share";

/** True while a session exists. Screens read this to decide what to offer. */
export let signedIn = false;

export function startApp(root: HTMLElement): void {
  applyToDocument();

  const outlet = h("main", { id: "outlet" });
  const header = h("header", { class: "app-header" });
  const nav = h("nav", { class: "app-nav" });

  mount(root, header, outlet, nav);

  const renderChrome = () => {
    mount(
      header,
      h("button", {
        class: "brand",
        text: t("appName"),
        on: { click: () => navigate("/") },
      }),
      h("div", { class: "header-actions" },
        // Language toggle shows the language you'd switch TO, not the current
        // one — a button labelled with the current state reads as a status.
        h("button", {
          class: "ghost",
          text: lang() === "en" ? "فا" : "EN",
          title: t("language"),
          on: { click: () => toggleLang() },
        }),
        h("button", {
          class: "ghost",
          text: signedIn ? t("navSettings") : t("signIn"),
          on: { click: () => navigate(signedIn ? "/settings" : "/signin") },
        }),
      ),
    );

    const path = currentRoute();
    const tab = (label: string, target: string) =>
      h("button", {
        class: path === target || (target !== "/" && path.startsWith(target)) ? "tab active" : "tab",
        text: label,
        on: { click: () => navigate(target) },
      });

    mount(nav, tab(t("navHome"), "/"), tab(t("navBrowse"), "/browse"));
  };

  renderChrome();

  startRouter({
    host: outlet,
    initial: "/",
    routes: [
      { pattern: "/", render: (_p, host) => renderHome(host) },
      { pattern: "/browse", render: (_p, host) => renderBrowse(host) },
      { pattern: "/browse/new", render: (_p, host) => renderCollectionForm(host) },
      { pattern: "/c/:id", render: (p, host) => renderCollection(p.id!, host) },
      { pattern: "/c/:id/edit", render: (p, host) => renderCollectionForm(host, p.id!) },
      { pattern: "/study/:id", render: (p, host) => renderStudy(p.id!, host) },
      { pattern: "/signin", render: (_p, host) => renderSignIn(host) },
      { pattern: "/share/:token", render: (p, host) => renderShareClaim(p.token!, host) },
      {
        pattern: "/settings",
        render: (_p, host) =>
          mount(
            host,
            h("div", { class: "screen" },
              h("h2", { text: t("navSettings") }),
              h("p", { class: "muted", text: versionLabel() }),
            ),
          ),
      },
      {
        // The test suite, as a normal route. Dynamically imported so its test
        // code and CSS stay in a separate chunk and never load for real users.
        pattern: "/diagnostics",
        render: async (_p, host) => {
          await import("../diagnostics.css");
          const { renderDiagnostics } = await import("../diagnostics");
          renderDiagnostics(host);
        },
      },
    ],
    fallback: (_p, host) =>
      mount(host, h("div", { class: "screen" }, h("h2", { text: "Not found" }))),
  });

  // Re-render chrome on nav so the active tab stays correct.
  addEventListener("hashchange", renderChrome);

  onLangChange(() => {
    renderChrome();
    refresh(); // re-render the current screen in the new language
  });

  onAuthChange((session) => {
    const next = !!session;
    const changed = next !== signedIn;
    signedIn = next;
    renderChrome();
    // Only re-render on an actual transition; onAuthChange also fires for token
    // refreshes, and reloading the screen mid-study session would lose the queue.
    if (changed) refresh();
  });
}
